import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import {
    submitNoPeanutPolicy,
    selectPolicyById,
    selectSubmitLoading
} from '../../store/noPeanutPolicy/noPeanutPolicySlice';
import { handleImageUpload } from '../../api/uploads';

const NoPeanutPolicyFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const existingPolicy = useSelector((state) => selectPolicyById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);

    const signaturePadRef = useRef(null);

    const [formData, setFormData] = useState({
        description: '',
        parent_signature: '',
        parent_signature_date: ''
    });

    useEffect(() => {
        if (isEditMode && existingPolicy) {
            setFormData({
                description: existingPolicy.description || '',
                parent_signature: existingPolicy.parent_signature || '',
                parent_signature_date: existingPolicy.parent_signature_date || ''
            });
        }
    }, [isEditMode, existingPolicy]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.description) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Please provide a description.'
            });
            return;
        }

        const hasNewSignature = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        const hasExistingSignature = isEditMode && existingPolicy?.parent_signature;

        if (!hasNewSignature && !hasExistingSignature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }

        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }

        try {
            const submitData = { ...formData };

            // Upload new parent signature to AWS if provided
            if (hasNewSignature) {
                const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
                const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
                const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
                const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
                submitData.parent_signature = signatureUrl;
            } else if (hasExistingSignature) {
                submitData.parent_signature = existingPolicy.parent_signature;
            }

            if (isEditMode) {
                submitData.id = id;
            }

            await dispatch(submitNoPeanutPolicy(submitData)).unwrap();

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `No Peanut Policy ${isEditMode ? 'updated' : 'created'} successfully!`
            });

            navigate('/dashboard/no-peanut-policy/list');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error || 'Failed to submit policy'
            });
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {isEditMode ? 'Edit' : 'Sign'} {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Policy Letter */}
                                <div className="alert alert-default" role="alert">
                                    <h5 className="alert-heading">Dear Parents,</h5>
                                    <p>
                                        This letter is to inform you that a student/teacher in your child's classroom has a severe peanut/nut allergy.
                                        Strict avoidance of peanut/nut products is the only way to prevent a life-threatening allergic reaction.
                                        We are asking your assistance in providing the student with a safe learning environment.
                                    </p>
                                    <p>
                                        If exposed to peanuts/nuts the student/teacher may develop a life-threatening allergic reaction that requires emergency medical treatment.
                                        The greatest potential for exposure at school is to peanut products and nut products. To reduce the risk of exposure,
                                        the classroom will be peanut/nut free. Please, do not send any peanut or tree nut containing products for your child to eat during snack in the classroom.
                                        Any exposure to peanuts or nuts through contact or ingestion can cause a severe reaction. If your child has eaten peanuts, peanut butter,
                                        or nuts prior to coming to school, please be sure your child's hands have been thoroughly washed prior to entering the school.
                                    </p>
                                    <p>
                                        We appreciate your support of these procedures. Please complete and return this form so that we are certain that every family has received this information.
                                        If you have any questions, please contact me.
                                    </p>
                                    <hr />
                                    <h6 className="text-danger">NO PEANUTS OR NUTS, PLEASE</h6>
                                    <p className="mb-0"><strong>Mary Domotor</strong><br />Supervisor</p>
                                </div>

                                {/* Acknowledgement */}
                                <div className="alert alert-light border" role="alert">
                                    <p className="mb-0">
                                        I have read and understand the peanut/nut free classroom procedures.
                                        I agree to do my part in keeping the classroom peanut and nut free.
                                    </p>
                                </div>

                                <hr className="my-4" />

                                {/* Description Field */}
                                {/* <h5 className="mb-3">Additional Comments (Optional)</h5>
                  <Row className="mb-3">
                    <Col md="12">
                      <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Any additional comments or concerns..."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <hr className="my-4" /> */}

                                {/* Signature Section */}
                                <h5 className="mb-3">Parent Signature</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        {isEditMode && existingPolicy?.parent_signature ? (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Existing Parent Signature</Form.Label>
                                                <div className="border rounded p-3 bg-light mb-3">
                                                    <img
                                                        src={existingPolicy.parent_signature}
                                                        alt="Existing Parent Signature"
                                                        style={{ maxWidth: '400px', maxHeight: '150px', display: 'block' }}
                                                    />
                                                    <p className="mt-2 mb-0 text-muted">
                                                        <strong>Date:</strong> {existingPolicy.parent_signature_date ? new Date(existingPolicy.parent_signature_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                                                    </p>
                                                </div>
                                                <p className="text-info mb-2"><small>You can update the signature by drawing a new one below:</small></p>
                                            </Form.Group>
                                        ) : null}
                                        <Form.Group className="mb-3">
                                            <Form.Label>{isEditMode && existingPolicy?.parent_signature ? 'New Parent Signature (Optional)' : 'Parent Signature *'}</Form.Label>
                                            <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px' }}>
                                                <SignatureCanvas
                                                    ref={signaturePadRef}
                                                    canvasProps={{
                                                        className: 'signature-canvas',
                                                        style: { width: '100%', height: '100%' }
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={handleClearSignature}
                                                type="button"
                                            >
                                                Clear Signature
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                    <Col md="6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Signature Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={formData.parent_signature_date}
                                                onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                max={new Date().toISOString().split('T')[0]}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Submit Buttons */}
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => navigate('/dashboard/no-peanut-policy/list')}
                                        disabled={submitLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={submitLoading}
                                    >
                                        {submitLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>{isEditMode ? 'Update' : 'Submit'} Policy</>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
export default NoPeanutPolicyFormComp;
